/* The extension, as part of the site. On every page it looks for the PathsAbroad extension in this browser and makes
   it match the site (packages/core extension-link.ts): connected to the student who is signed in, with no code to
   type, and to nobody when nobody is. Pages read where things stand with useExtension(). */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { isNativeApp, openExtensionLink, syncExtension, type ExtensionLink, type ExtensionLinkResponse, type ExtStatus, type ExtSyncState, type PairResponse } from '@pathabroad/core/browser';
import { api } from '../lib/api';
import { useT } from './i18n';
import { useSession } from './session';

export type ExtView = {
  /* checking: looking for it; connecting: handing it a code; the rest as syncExtension says */
  state: 'checking' | 'connecting' | ExtSyncState;
  status: ExtStatus | null;
  error?: string;
  retry: () => void;
};

const ExtensionContext = createContext<ExtView>({ state: 'checking', status: null, retry: () => {} });
export const useExtension = () => useContext(ExtensionContext);

/* one tab at a time: two open tabs would otherwise both ask for a code */
const withLock = <T,>(fn: () => Promise<T>): Promise<T> => {
  const locks = (globalThis.navigator as Navigator & { locks?: { request: (name: string, cb: () => Promise<T>) => Promise<T> } }).locks;
  return locks ? locks.request('pathsabroad-extension', fn) : fn();
};

export function ExtensionProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const { lang } = useT();
  const [view, setView] = useState<Omit<ExtView, 'retry'>>({ state: 'checking', status: null });
  const link = useRef<ExtensionLink | null>(null);
  const [round, setRound] = useState(0);

  useEffect(() => {
    const l = openExtensionLink(window);
    link.current = l;
    const off = l.subscribe(status => setView(v => ({ ...v, status })));
    return () => { off(); l.close(); link.current = null; };
  }, []);

  /* who is signed in, as far as the extension cares */
  const who = session.status === 'in' ? `in:${session.me.user.id}:${session.me.plans.apply ? 'apply' : 'free'}` : session.status;
  const langRef = useRef(lang);
  langRef.current = lang;

  useEffect(() => {
    const l = link.current;
    if (!l || session.status === 'loading' || session.status === 'error') return;
    /* the phone app fills applications itself: there is no extension in it */
    if (isNativeApp()) { setView(v => ({ ...v, state: 'missing' })); return; }
    let live = true;
    const signedIn = session.status === 'in';
    const apply = session.status === 'in' && session.me.plans.apply;
    void syncExtension(l, {
      signedIn, apply, lang: langRef.current, lock: withLock,
      mintCode: async () => {
        if (live) setView(v => ({ ...v, state: 'connecting' }));
        return (await api.post<PairResponse>('/api/extension/pair')).code;
      },
      isLinked: async id => (await api.get<ExtensionLinkResponse>(`/api/extension/link?id=${encodeURIComponent(id)}`)).linked === true,
    }).then(r => { if (live) setView(v => ({ ...v, state: r.state, error: r.error })); },
      () => { if (live) setView(v => ({ ...v, state: 'error', error: 'server' })); });
    return () => { live = false; };
  }, [who, round]); // eslint-disable-line react-hooks/exhaustive-deps

  /* the extension speaks the site's language */
  useEffect(() => { void link.current?.ping(lang); }, [lang]);

  const retry = useCallback(() => { setView(v => ({ ...v, state: 'checking', error: undefined })); setRound(n => n + 1); }, []);
  const value = useMemo(() => ({ ...view, retry }), [view, retry]);
  return <ExtensionContext.Provider value={value}>{children}</ExtensionContext.Provider>;
}
